// Per-workspace icon and accent color, keyed by the API's `icon` string (workspaces.icon column,
// ROADMAP.md v0.6.2). Deliberately NOT platform_db data — colors are pure presentation, not
// something ARCHITECTURE.md §3's workspaces schema tracks, and an unknown/new `icon` value still
// needs to render *something* sensible rather than crash, which a DB-required color can't do.
import type { ComponentType } from 'react';
import {
  CropGrowthIcon,
  FieldOperationsIcon,
  GeneralWorkspaceIcon,
  LogisticsIcon,
  PhotoLayerIcon,
  WaterBoreholeIcon,
  WorkspacesIcon,
  type IconProps,
} from '../../components/icons/Icons';

interface WorkspaceStyle {
  Icon: ComponentType<IconProps>;
  /** Saturated brand-ish color for the card's centered icon. */
  accent: string;
  /** Light tint of `accent` used as the card thumbnail's gradient origin (over --surface-2). */
  accent2: string;
}

const STYLES: Record<string, WorkspaceStyle> = {
  general: { Icon: GeneralWorkspaceIcon, accent: '#2f7d4f', accent2: '#d5ecdd' },
  'field-photos': { Icon: PhotoLayerIcon, accent: '#d94f63', accent2: '#fadde2' },
  logistics: { Icon: LogisticsIcon, accent: '#3b6fd6', accent2: '#dbe5fa' },
  'crop-growth': { Icon: CropGrowthIcon, accent: '#bf8f2d', accent2: '#f6e8c8' },
  'water-borehole': { Icon: WaterBoreholeIcon, accent: '#2fb1c9', accent2: '#d3eef3' },
  'field-operations': { Icon: FieldOperationsIcon, accent: '#7a5fd6', accent2: '#e4ddf7' },
};

/** A neutral fallback for any `icon` value the client doesn't recognise — new workspaces added
 * server-side (platform_db) render correctly, just without a bespoke glyph, rather than crash. */
const FALLBACK: WorkspaceStyle = { Icon: WorkspacesIcon, accent: '#5a6472', accent2: '#e2e5ea' };

export function styleForWorkspace(icon: string | null): WorkspaceStyle {
  return (icon && STYLES[icon]) || FALLBACK;
}
